// At-rest storage for the tenant private key.
//
// What is stored is the non-extractable `CryptoKey` itself, written
// straight into IndexedDB by structured clone. Not ciphertext, not
// wrapped bytes — the handle. IndexedDB preserves a `CryptoKey` across
// reloads and across restarts, and it preserves `extractable: false`
// with it, so script on this origin can USE the stored key and can never
// READ it. That is the only property that ever mattered, and it is now
// provided by the browser rather than reconstructed by us.
//
// **Why this replaced the passkey design.** The previous shape was
// `AES-GCM(WebAuthn-PRF-derived key, PKCS8)` with a PBKDF2 passphrase
// fallback: an enrolment ceremony, a credential to manage, two
// derivations, a discriminated record, and a long tail of
// authenticator-specific failure modes — all of it machinery for
// protecting a single credential inside a product. The cross-device
// story that justified the cost does not survive contact with reality
// either: the encrypted blob lived in IndexedDB, which is per-browser,
// so a second machine still needed the recovery file. We were paying a
// ceremony's complexity for a per-device guarantee. This is the same
// guarantee with none of the machinery, and zero prompts after one
// recovery-file paste per browser.
//
// **What is deliberately given up: cross-device.** Unlocking on a second
// machine without the server being able to decrypt requires a secret the
// server never sees, and only an authenticator supplies one. Per-device
// is the accepted cost; the recovery file remains the way a new machine
// is brought up.
//
// **What this cannot do, and the consequence.** A non-extractable handle
// is a one-way door: nothing in this module can hand back PKCS8 bytes,
// because WebCrypto will not produce them. Operations whose plaintext IS
// the key — sealing the KEK to a daemon, rotating the tenant key — can
// therefore never be served from storage. They need the recovery file.
// That is a real cost and it is stated here rather than discovered at
// the call site.
//
// **No work at module load.** `app/control-admin` server-renders on
// Cloudflare Workers, where `indexedDB` does not exist. Touching it at
// import time would break SSR for every route that transitively imports
// this file — so the database is opened lazily, inside each call, and
// every exported function degrades gracefully (a `VaultKeyStoreError`
// with `reason: 'unavailable'`, or a falsy answer for the queries)
// rather than exploding when there is no IndexedDB: SSR, a locked-down
// profile, or Safari private browsing.
//
// **The record is keyed on rootId AND the tenant public key.** This is
// load-bearing, not belt-and-braces. After a key rotation the root id is
// unchanged but the stored handle is the *old* private key; a lookup on
// rootId alone would hand back a key that no longer matches the tenant,
// and the mismatch would surface much later as unexplained decryption
// failures. Keying on the public key too means a rotated key or a
// different tenant simply misses, and the UI falls back to the recovery
// file — the correct outcome.
//
// SECURITY: nothing in this file logs, prints, or puts into an error
// message any key material or plaintext.
import { open as openSealedBox, seal, unb64u, utf8 } from '@stoaria/vault-core'
import {
  VaultKeyStoreError,
  type StoredKeyRecord,
  deleteRecord,
  indexedDbOrNull,
  putRecord,
  readRecord,
} from './vault-key-store-db.ts'

// The store's public vocabulary lives one file over but belongs to this
// module's interface; re-exported by name (not `export *`) so the surface
// stays explicit.
export {
  VaultKeyStoreError,
  countRecordsForRoot,
  forget,
  forgetAll,
} from './vault-key-store-db.ts'
export type { VaultKeyStoreReason } from './vault-key-store-db.ts'

/** X25519, the tenant KEK's algorithm. Same curve the sealed-box format
 * in `@stoaria/vault-core` is built on. */
const X25519_NAME = 'X25519'

/** Raw X25519 public keys are 32 bytes. A `tenantPubkey` that decodes to
 * anything else is not a tenant public key, and the self-test below
 * cannot even be attempted with it. */
const PUBLIC_KEY_BYTES = 32

/**
 * The fixed plaintext the load-time self-test seals and re-opens.
 *
 * Fixed rather than random because there is nothing to hide: it is a
 * constant in the bundle, it is sealed under a fresh ephemeral key and
 * nonce every time (so two self-tests never produce the same box), and
 * the only thing the test asks is whether the bytes come back. A random
 * probe would prove exactly the same thing and read as though it were
 * doing more.
 */
const SELF_TEST_PROBE = utf8('stoaria.vault-key self-test v1')

/** Whether a key is stored for exactly this root *and* this tenant public
 * key. False — never a throw — when IndexedDB is absent, so a component
 * can call this during SSR and simply render the recovery-file path.
 *
 * This is the cheap "is there anything here" probe for deciding what the
 * unlock control should offer. It does NOT run the self-test: that costs
 * two ECDH operations and, more importantly, its failure mode is
 * "discard the record", which a render-time probe has no business
 * triggering. A record that passes here and fails `loadStoredKey` is the
 * expected shape of the tampering case, and the UI falls back to the
 * paste.
 */
export async function hasStoredKey(
  rootId: string,
  tenantPubkey: string,
): Promise<boolean> {
  return (await readRecord(rootId, tenantPubkey)) !== null
}

export interface StoreParams {
  rootId: string
  /** base64url tenant PUBLIC key — half of the record's identity. */
  tenantPubkey: string
  /**
   * The tenant private key as a non-extractable X25519 handle: normally
   * the very object `adoptImportedKey` just adopted, so what is on disk
   * and what this tab is using are the same key rather than two imports
   * that ought to agree.
   *
   * It is validated, not trusted — see `assertUsableHandle`. In
   * particular an EXTRACTABLE handle is refused, because
   * `extractable: false` is the entire security claim of the stored
   * record and a parameter of this type is the one place a caller could
   * quietly void it.
   */
  key: CryptoKey
}

/**
 * Persists the handle, replacing any record already held for this
 * (rootId, tenantPubkey) pair.
 *
 * Throws `VaultKeyStoreError` — switch on `.reason`. The modelled
 * failures are `'unavailable'` (no IndexedDB), `'write-failed'`, and
 * `'corrupt'` for a handle that is not a usable X25519 private key. With
 * no enrolment and no derivation there is nothing else left to go wrong,
 * which is most of the point of the change.
 */
export async function store(params: StoreParams): Promise<void> {
  if (indexedDbOrNull() === null)
    throw new VaultKeyStoreError(
      'unavailable',
      'this browser has no IndexedDB, so the key cannot be stored',
    )

  // Checked before the write, not after the read, so a bad handle never
  // reaches the disk in the first place. `loadStoredKey` checks again
  // anyway: what is on disk can change without passing through here.
  assertUsableHandle(params.key)

  const record: StoredKeyRecord = {
    rootId: params.rootId,
    tenantPubkey: params.tenantPubkey,
    key: params.key,
    storedAt: Date.now(),
  }
  await putRecord(record)
}

/**
 * Returns the stored tenant key as a handle, ready to adopt as the tab's
 * session (`adoptKeyHandle`) or to pair with its public half for
 * `open()`.
 *
 * This is the whole read path. There is no `unwrap()` any more and there
 * cannot be: see the module docblock.
 *
 * Throws `VaultKeyStoreError`; switch on `.reason`. `'not-found'` means
 * nothing is stored for this (root, tenant key) pair — including the
 * rotated-away case — and is the signal to fall back to the recovery
 * file rather than an error to show.
 */
export async function loadStoredKey(
  rootId: string,
  tenantPubkey: string,
): Promise<CryptoKey> {
  if (indexedDbOrNull() === null)
    throw new VaultKeyStoreError(
      'unavailable',
      'this browser has no IndexedDB, so no key could have been stored',
    )

  const record = await readRecord(rootId, tenantPubkey)
  if (record === null)
    throw new VaultKeyStoreError(
      'not-found',
      'no stored key for this vault root and tenant key',
    )

  const publicKey = decodePublicKey(tenantPubkey)
  assertUsableHandle(record.key)
  await assertKeyMatchesPublicKey(rootId, tenantPubkey, record.key, publicKey)
  return record.key
}

/** Decodes the base64url tenant public key, or classifies it as a corrupt
 * record. `unb64u` throws on input `atob` rejects, and a caller should
 * not have to tell that apart from a database failure. */
function decodePublicKey(tenantPubkey: string): Uint8Array {
  let publicKey: Uint8Array
  try {
    publicKey = unb64u(tenantPubkey)
  } catch (cause) {
    throw new VaultKeyStoreError(
      'corrupt',
      'the tenant public key for this record is not valid base64url',
      { cause },
    )
  }
  if (publicKey.length !== PUBLIC_KEY_BYTES)
    throw new VaultKeyStoreError(
      'corrupt',
      `a tenant public key must be ${String(PUBLIC_KEY_BYTES)} bytes, ` +
        `got ${String(publicKey.length)}`,
    )
  return publicKey
}

/** Structural gate on a handle, before the cryptographic one. Run on the
 * way in (`store`) and on the way out (`loadStoredKey`), because the two
 * guard different things: a caller mistake and a changed database.
 *
 * These three facts are the ones a mistake shows up as, and each
 * produces a confusing failure further down if it is let through. A
 * public key or a non-X25519 key fails inside `deriveBits` with a
 * WebCrypto `InvalidAccessError`; a missing `deriveBits` usage fails the
 * same way; and an EXTRACTABLE key would work perfectly while quietly
 * voiding the only guarantee this module makes. Algorithm names, key
 * types and usages are public metadata, so naming them in the message is
 * safe and is what makes the failure diagnosable. */
function assertUsableHandle(key: CryptoKey): void {
  if (key.type !== 'private' || key.algorithm.name !== X25519_NAME)
    throw new VaultKeyStoreError(
      'corrupt',
      `the handle is a ${key.algorithm.name} ${key.type} key, ` +
        'not an X25519 private key',
    )
  if (!key.usages.includes('deriveBits'))
    throw new VaultKeyStoreError(
      'corrupt',
      "the handle was imported without the 'deriveBits' usage",
    )
  if (key.extractable)
    throw new VaultKeyStoreError(
      'corrupt',
      'the handle is extractable, so storing it would void the one ' +
        'guarantee the vault key store makes',
    )
}

/**
 * Proves the stored handle really is the private half of the stored
 * `tenantPubkey`, by sealing a fixed probe to the public key and opening
 * it with the handle.
 *
 * **This is the one hazard the new record shape introduces, and it is
 * what closes it.** The old record was AES-GCM, so its identity could be
 * bound into the tag as additional authenticated data; a relabelled
 * record simply failed to decrypt. A bare `CryptoKey` has no tag and no
 * AAD, so nothing stops a script on this origin — IndexedDB is ordinary
 * same-origin storage — from swapping the handle for one it generated
 * while leaving `tenantPubkey` pointing at the real tenant. The session
 * would then pair an attacker's private key with the real public key.
 * `open()` would merely fail, which is survivable; `sealKekToDaemon`
 * would seal the WRONG key to a daemon, which is not. (An earlier review
 * on this branch caught the same class of defect, which is why it is
 * spelled out at this length.)
 *
 * The self-test is strictly better than the AAD it replaces. AAD proved
 * that a label had not been edited. This proves the keypair actually
 * matches — the property we wanted the label to stand for — and it costs
 * two ECDH operations once per load.
 *
 * A failure DISCARDS the record. It is either tampered with or
 * irreparably wrong; either way it can never open anything, and leaving
 * it would have the UI offer a stored unlock that is guaranteed to fail
 * on every subsequent load.
 *
 * SECURITY: the probe is a public constant and the error says only that
 * the pair does not match. Nothing derived from the key reaches the
 * message.
 */
async function assertKeyMatchesPublicKey(
  rootId: string,
  tenantPubkey: string,
  key: CryptoKey,
  publicKey: Uint8Array,
): Promise<void> {
  const failure = await selfTestFailure(key, publicKey)
  if (failure === null) return
  await deleteRecord(rootId, tenantPubkey)
  throw new VaultKeyStoreError(
    'key-mismatch',
    'the key stored on this device is not the private half of this ' +
      "vault's tenant key, so it has been discarded — unlock with the " +
      'recovery file',
    { cause: failure },
  )
}

/** Runs the round trip and reports what went wrong, or null if it worked.
 * Separated from the throwing wrapper so the `delete`-then-throw sequence
 * above reads as the single decision it is. */
async function selfTestFailure(
  key: CryptoKey,
  publicKey: Uint8Array,
): Promise<Error | null> {
  try {
    const sealed = await seal(SELF_TEST_PROBE, publicKey)
    const opened = await openSealedBox(sealed, { key, publicKey })
    if (!bytesEqual(opened, SELF_TEST_PROBE))
      // Not reachable through AES-GCM, which rejects rather than
      // returning the wrong plaintext. Checked anyway: the test is
      // worthless if its assertion is "it did not throw".
      return new Error('the self-test probe did not round-trip')
    return null
  } catch (cause) {
    // Expected: a `SealedBoxOpenError` when the handle is not the right
    // private key. Returned rather than rethrown so the caller discards
    // the record first.
    return cause instanceof Error ? cause : new Error(String(cause))
  }
}

/** Plain, non-constant-time comparison, which is correct here: both
 * operands are the same public constant, so there is no secret for a
 * timing difference to leak. */
function bytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false
  return left.every((byte, index) => byte === right[index])
}
